import { useEffect, useState } from "react";
import { Button, Container, Grid } from "@material-ui/core";
import Api from "lib/api/httpRequestHelper";
import getMonthByNumber from "utils/get-month-by-number";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import LoadingData from "components/Loading/LoadingData";
import HeaderClientes from "components/_refactor/headers/clients";
import FilterCard from "components/_refactor/filter-card";
import MonthYearField from "components/_refactor/fields/month-year";
import StructureFields from "components/_refactor/fields/structure";
import TableActions from "./components/table-actions";
import DialogDetail from "./components/detail-dialog";
import DeleteDialog from "./components/delete-dialog";
import DialogCreate from "./components/create-dialog";

import { Add } from "@mui/icons-material";

const MONTH_INDEX = new Date().getMonth() + 1;
const YEAR = new Date().getFullYear();

export default function GestaoFundoPropaganda({ view = "admin" }) {
  const isClient = view === "client";

  const [data, setData] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [page, setPage] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const [openViewDialog, setOpenViewDialog] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const [structureFilters, setStructureFilters] = useState({});
  const [structureLoading, setStructureLoading] = useState([]);
  const [month, setMonth] = useState({
    value: MONTH_INDEX,
    label: getMonthByNumber(MONTH_INDEX),
  });
  const [year, setYear] = useState({ value: YEAR, label: String(YEAR) });
  const [clearFilters, setClearFilters] = useState(false);

  const snackbar = useSnackbar();

  const handleSearch = async ({ page = 0, sizePerPage = 10 }) => {
    try {
      setIsLoading(true);
      const response = await Api.get(
        "advertising-list-files",
        "franquias",
        `?page=${page}&pageSize=${sizePerPage}&idPessoaLoja=${
          structureFilters?.idLoja || ""
        }&periodo=${getMonthByNumber(month.value)}/${year.value}`,
        true
      );
      setData(response.Result.Files);
      setTotalRegistros(response.Result.TotalRecords);
      setPage(response.Result.Pagina || 0);
      setSizePerPage(response.Result.PageSize || 10);
    } catch (error) {
      snackbar.showMessage("Erro ao buscar os dados.", {
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = (fileUrl) => {
    try {
      snackbar.showMessage("Iniciando o download do arquivo.", {
        severity: "info",
      });
      setIsLoading(true);
      window.open(fileUrl, "_blank");
    } catch (error) {
      snackbar.showMessage("Erro ao fazer o download do arquivo.", {
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (idFranquiaFundoPropaganda) => {
    console.log(idFranquiaFundoPropaganda);
    try {
      const response = await Api.put(
        "advertising-edit-file",
        "franquias",
        { idFranquiaFundoPropaganda },
        true
      );
      if (response.Success) {
        snackbar.showMessage("Arquivo excluído com sucesso.", {
          severity: "success",
        });
        handleSearch({ page: 0, sizePerPage: 10 });
      }
      setOpenDeleteDialog(null);
    } catch (error) {
      snackbar.showMessage(
        "Não foi possível excluir o arquivo. Tente novamente.",
        { severity: "error" }
      );
    }
  };

  const handleClearFilters = () => {
    setMonth("");
    setYear("");
    setClearFilters(true);
    setTimeout(() => {
      setClearFilters(false);
    }, 300);
  };

  const onTableChange = (page, sizePerPage) => {
    handleSearch({ page, sizePerPage });
  };

  useEffect(() => {
    handleSearch({ page: 0, sizePerPage: 10 });
  }, []);

  return (
    <>
      <HeaderClientes
        section={
          isClient ? "Fundo de Propaganda" : "Gestão de Fundo de Propaganda"
        }
        subsection={isClient ? "Financeiro" : "Administração de Franquias"}
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <FilterCard
          handleSearch={handleSearch}
          searching={isLoading}
          handleClearFilters={handleClearFilters}
        >
          {!isClient && (
            <StructureFields
              clearFilters={clearFilters}
              onStructureFieldsChange={setStructureFilters}
              disableAll={!!structureLoading.length}
              onStructureFieldsLoad={setStructureLoading}
              requiredFields={["store"]}
              hideFields={["brand", "channel", "representative"]}
              handleSearch={() => {}}
              franquias
            />
          )}
          <MonthYearField
            month={month}
            handleMonthChange={setMonth}
            year={year}
            handleYearChange={setYear}
            disabled={isLoading}
            displayEmpty={false}
            md={8}
          />
        </FilterCard>
        {isLoading ? (
          <Grid container>
            <Grid item xs={12}>
              <LoadingData
                searching={isLoading}
                label="Carregando os dados..."
              />
            </Grid>
          </Grid>
        ) : (
          <>
            {!isClient && !data.length && (
              <Grid container justify="flex-end">
                <Grid
                  item
                  xs={6}
                  md={4}
                  lg={2}
                  style={{ marginBottom: "1rem" }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenCreateDialog(true)}
                  >
                    <Add /> Criar Ação
                  </Button>
                </Grid>
              </Grid>
            )}
            <TableActions
              data={data}
              totalRegistros={totalRegistros}
              page={page}
              sizePerPage={sizePerPage}
              onTableChange={onTableChange}
              onAction={handleDownload}
              onView={(url) => setOpenViewDialog(url)}
              onDelete={(row) => setOpenDeleteDialog(row)}
              onCreate={() => setOpenCreateDialog(true)}
              isClient={isClient}
            />
          </>
        )}
        {openViewDialog && (
          <DialogDetail
            onClose={() => setOpenViewDialog(null)}
            url={openViewDialog}
          />
        )}

        {openDeleteDialog && (
          <DeleteDialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(null)}
            onDelete={() =>
              handleDelete(openDeleteDialog.IdFranquiaFundoPropaganda)
            }
            loading={isLoading}
            name={openDeleteDialog.NomeArquivo}
          />
        )}

        {openCreateDialog && (
          <DialogCreate
            onClose={() => setOpenCreateDialog(null)}
            handleSearch={handleSearch}
          />
        )}
      </Container>
    </>
  );
}
